// =============================================================================
// Subset check — funcion PURA e isomorfica. Sin imports mas alla de tipos.
//
// Es EL algoritmo del guardrail anti-escalada. Vive aca, en el paquete
// compartido, para que worker y dashboard usen exactamente el mismo codigo: el
// snapshot `assignable` que calcula el server y lo que el editor de roles
// habilita en el cliente tienen que coincidir de forma trivial, no "por
// casualidad de que las dos implementaciones digan lo mismo".
//
// Porteado de `canPlatformDo` de ETP (`@etp/auth-shared/can-platform-do.ts`).
// =============================================================================

import type { PermissionsMap } from "./permissions";

/**
 * Puede un actor con `possessed` otorgar o ejecutar `required`?
 *
 * `true` sii cada recurso de `required` existe en `possessed` y todas sus
 * acciones estan contenidas:
 *   - `required` vacio → siempre `true` (no se pide nada).
 *   - Recurso de `required` ausente en `possessed` → `false`.
 *   - Accion ausente del array de `possessed[recurso]` → `false`.
 *
 * NO reemplaza el enforcement del server. El dashboard la usa para anotar la UI
 * (habilitar botones, marcar roles asignables); el worker SIEMPRE re-chequea con
 * su propia llamada antes de mutar.
 */
export function canPrintflowDo(possessed: PermissionsMap, required: PermissionsMap): boolean {
  return Object.entries(required).every(([recurso, acciones]) => {
    // Object.hasOwn antes de leer: una clave como "constructor" o "toString"
    // devolveria una funcion del prototype en vez de undefined, y el
    // `.includes` de abajo explotaria con TypeError. Fail-closed.
    const owned = Object.hasOwn(possessed, recurso) ? possessed[recurso] : undefined;
    return Array.isArray(owned) && acciones.every((accion) => owned.includes(accion));
  });
}

/**
 * Union de varios mapas de permisos.
 *
 * PrintFlow tiene roles ACUMULATIVOS: un usuario puede tener N roles y sus
 * permisos efectivos son la union de todos (`spec_implemented.md` §2.3 — ej.
 * Lead Produccion + Ventas). Es la diferencia principal con ETP, donde un
 * usuario tiene un solo rol de plataforma.
 */
export function unionPermissions(maps: readonly PermissionsMap[]): PermissionsMap {
  const out: PermissionsMap = {};
  for (const map of maps) {
    for (const [recurso, acciones] of Object.entries(map)) {
      if (!Object.hasOwn(map, recurso) || !Array.isArray(acciones)) continue;
      let acc = out[recurso];
      if (acc === undefined) {
        acc = [];
        out[recurso] = acc;
      }
      for (const accion of acciones) {
        if (!acc.includes(accion)) acc.push(accion);
      }
    }
  }
  return out;
}

/** Aplana un mapa de permisos a los codigos `recurso.accion`. */
export function toCodigos(map: PermissionsMap): string[] {
  return Object.entries(map).flatMap(([recurso, acciones]) =>
    Array.isArray(acciones) ? acciones.map((accion) => `${recurso}.${accion}`) : [],
  );
}
